import { SupportedLanguage } from 'humanize-duration';

export function getHostWithoutSubdomain(host = window.location.host) {
  return host.split('.').slice(1).join('.');
}

export function getAppUrlWithNewSubdomain(subdomain: string) {
  const hostWithoutSubdomain = window.location.host.slice(window.location.host.indexOf('.') + 1);
  return `https://${subdomain}.${hostWithoutSubdomain}`;
}

export function getHotelQRCodeLink(origin?: string) {
  return `${origin || window.location.origin}/app/redirect`;
}

export type SupportedLocalesCode = 'en' | 'de' | 'it' | 'fr' | 'pl';

export type SupportedLocale = {
  displayName: string;
  value: SupportedLocalesCode;
};

export type AvailableLocale = {
  keyLocale: string;
  displayName: string;
  value: SupportedLocalesCode;
};

export type LocalizedValueMap<TData> = {
  [key: string]: TData;
};

export const SUPPORTED_LOCALES: SupportedLocale[] = [
  {
    displayName: 'English',
    value: 'en',
  },
  {
    displayName: 'Deutsch',
    value: 'de',
  },
  {
    displayName: 'Italiano',
    value: 'it',
  },
  {
    displayName: 'Français',
    value: 'fr',
  },
  {
    displayName: 'Polski',
    value: 'pl',
  },
];

export const ALL_LOCALES: AvailableLocale[] = [
  {
    keyLocale: 'English',
    displayName: 'English',
    value: 'en',
  },
  {
    keyLocale: 'German',
    displayName: 'Deutsch',
    value: 'de',
  },
  {
    keyLocale: 'Italian',
    displayName: 'Italiano',
    value: 'it',
  },
  {
    keyLocale: 'French',
    displayName: 'Français',
    value: 'fr',
  },
  {
    keyLocale: 'Polish',
    displayName: 'Polski',
    value: 'pl',
  },
];

export const getAvailableLocales = (activeLanguage: SupportedLanguage[]) => {
  return ALL_LOCALES.filter((locale) => activeLanguage.includes(locale.value));
};
export function isSupportedLocale(locale: SupportedLocalesCode) {
  return SUPPORTED_LOCALES.find(
    (suportedLocale: SupportedLocale) => suportedLocale.value === locale
  );
}

export type TLocalized<T> = {
  locale?: string | null;
  id?: string | null;
  localizations:
    | (
        | ({
            locale?: string | null;
            id?: string | null;
          } & T)
        | null
      )[]
    | null;
} & T & { [key: string]: any };

interface InjectGlobalValueIntoLocalizedObjectOptions<T = any> {
  key: string;
  value: T;
}

export function injectGlobalValueIntoLocalizedObject<T = any, TObject = any>(
  values: LocalizedValueMap<T>,
  { key, value }: InjectGlobalValueIntoLocalizedObjectOptions<TObject>
) {
  return Object.keys(values).reduce((newObj, langKey) => {
    return {
      ...newObj,
      [langKey]: {
        ...values[langKey],
        [key]: value,
      },
    };
  }, {} as LocalizedValueMap<T>);
}

export function mergeLocalizedObjects<T1 = any, T2 = any>(
  obj1: LocalizedValueMap<T1>,
  obj2: LocalizedValueMap<T2>
) {
  return Object.keys(obj1).reduce((newObj, langKey) => {
    return {
      ...newObj,
      [langKey]: {
        ...obj1[langKey],
        ...obj2[langKey],
      },
    };
  }, {} as LocalizedValueMap<T1>);
}

interface NormalizeLocalizedValuesOptions {
  /**
   * Properties to copy from the
   * main locale to every other locale.
   * Needs to be done for relations
   * when using localization.
   */
  globalProperties?: string[];
}

/**
 * Normalizes the data structure
 * from Strapi for more straightforward
 * use in the hotel dashboard.
 * Returns an object with a key
 * for each language containing
 * the localized object.
 */
export function normalizeLocalizedValues<T = any>(
  values?: TLocalized<T> | null,
  options: NormalizeLocalizedValuesOptions = {}
): {
  [key: string]: T & {
    originalId?: string | null;
  };
} {
  if (!values) return {};

  const { globalProperties = [] } = options;

  const localeObj: { [key: string]: T & { originalId?: string | null } } = {};

  if (values.locale) {
    localeObj[values.locale] = { ...values, originalId: values.id };
  }

  const propertiesToAddGlobally = globalProperties.reduce((obj, key) => {
    return {
      ...obj,
      [key]: values[key],
    };
  }, {} as Partial<T>);

  if (values.localizations) {
    for (const localeValue of values.localizations) {
      if (localeValue?.locale) {
        localeObj[localeValue.locale] = {
          ...localeValue,
          ...propertiesToAddGlobally,
          originalId: localeValue.id,
          id: values.id,
        };
      }
    }
  }

  return localeObj;
}

export interface NormalizedLocalizedArrayOptions extends NormalizeLocalizedValuesOptions {
  desiredLocale: string;
}

/**
 * Normalize an array of localized objects
 * to only contain the wished locale
 */
export function normalizedLocalizedArray<T = any>(
  values: (TLocalized<T> | null | undefined)[],
  optionsOrLocale: string | NormalizedLocalizedArrayOptions
) {
  let normalizedOptions: NormalizeLocalizedValuesOptions = {};
  let desiredLocale = '';
  if (typeof optionsOrLocale === 'string') {
    desiredLocale = optionsOrLocale;
  } else {
    const { desiredLocale: desiredLocaleFromOptions, ...options } = optionsOrLocale;
    desiredLocale = desiredLocaleFromOptions;
    normalizedOptions = options;
  }

  const mappedLocales = values
    ?.map((prop) => normalizeLocalizedValues(prop, normalizedOptions))
    ?.map((prop) => prop[desiredLocale])
    .filter(Boolean);

  return mappedLocales;
}

export * from './array';
export * from './images';
